<template>
  <div class="w-full min-w-0 overflow-x-hidden">
    <section id="home" class="scroll-mt-24 min-h-[calc(100vh-4rem)]">
      <div class="page-container py-8 sm:py-12 lg:py-20">
        <div class="glass-card w-full min-w-0 p-4 sm:p-8 lg:p-10 max-w-5xl hover:shadow-glass-lg transition-shadow duration-500">
          <div class="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-14 items-center">
            <div class="flex justify-center lg:justify-start animate-slide-up">
              <ProfileAvatar :src="images.profile" alt="Profile photo" size="xl" glow glass />
            </div>

            <div class="animate-slide-up min-w-0 w-full">
              <BaseBadge variant="primary" class="mb-5">Available for freelance</BaseBadge>

              <p class="text-sm text-muted mb-2">I build software that works</p>
              <h2 class="text-2xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight leading-tight mb-3 flex flex-col sm:flex-row sm:flex-wrap sm:items-baseline gap-x-2 gap-y-1">
                <span class="shrink-0">I'm a</span>
                <RoleRotator :roles="profile.roles" class="min-w-0 max-w-full" />
              </h2>
              <p class="text-sm sm:text-base text-muted max-w-xl break-words">{{ profile.bio }}</p>

              <div class="mt-6 sm:mt-8 glass-subtle rounded-xl px-4 sm:px-5 py-4 max-w-xl w-full min-w-0">
                <p class="text-sm text-muted italic leading-relaxed break-words">"{{ profile.verse }}"</p>
              </div>

              <div class="flex flex-col sm:flex-row flex-wrap gap-3 mt-6 sm:mt-8">
                <BaseButton
                  class="w-full sm:w-auto"
                  variant="primary"
                  size="lg"
                  tag="router-link"
                  :to="{ path: '/', hash: '#work' }"
                >
                  View Live Products
                  <AppIcon name="Briefcase" :size="18" />
                </BaseButton>
                <BaseButton
                  class="w-full sm:w-auto"
                  variant="secondary"
                  size="lg"
                  tag="router-link"
                  :to="{ path: '/', hash: '#resume' }"
                >
                  View Resume
                  <AppIcon name="FileText" :size="18" />
                </BaseButton>
                <BaseButton
                  class="w-full sm:w-auto"
                  variant="secondary"
                  size="lg"
                  tag="router-link"
                  :to="{ path: '/', hash: '#contact' }"
                >
                  Contact Me
                  <AppIcon name="Mail" :size="18" />
                </BaseButton>
              </div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6 sm:mt-8 max-w-5xl w-full min-w-0">
          <div
            v-for="stat in stats"
            :key="stat.label"
            class="glass-card p-4 sm:p-5 min-w-0 hover:shadow-glass-lg hover:border-primary/20 transition-all duration-300"
          >
            <p class="text-xl sm:text-2xl font-semibold text-foreground">{{ stat.value }}</p>
            <p class="text-[11px] sm:text-xs text-muted mt-1.5 leading-snug">{{ stat.label }}</p>
          </div>
        </div>

        <div class="flex justify-center mt-8 sm:mt-10 max-w-5xl w-full">
          <router-link
            :to="{ path: '/', hash: '#about' }"
            class="inline-flex flex-col items-center gap-1 text-xs text-muted hover:text-primary transition-colors"
          >
            <span>Scroll to explore</span>
            <AppIcon name="ChevronRight" :size="18" class="rotate-90 animate-bounce" />
          </router-link>
        </div>
      </div>
    </section>

    <section id="about" class="scroll-mt-24 border-t border-white/30 dark:border-white/10">
      <AboutPage embedded />
    </section>

    <section id="work" class="scroll-mt-24 border-t border-white/30 dark:border-white/10">
      <WorkPage embedded />
    </section>

    <section id="resume" class="scroll-mt-24 border-t border-white/30 dark:border-white/10">
      <ResumePage embedded />
    </section>

    <section id="contact" class="scroll-mt-24 border-t border-white/30 dark:border-white/10">
      <ContactPage embedded />
    </section>
  </div>
</template>

<script>
import { siteState } from '@/composables/useSiteData'
import { images } from '@/data/images'
import AboutPage from '@/pages/About.vue'
import WorkPage from '@/pages/Work.vue'
import ResumePage from '@/pages/Resume.vue'
import ContactPage from '@/pages/Contact.vue'
import ProfileAvatar from '@/components/ui/ProfileAvatar.vue'
import RoleRotator from '@/components/ui/RoleRotator.vue'
import BaseBadge from '@/components/ui/BaseBadge.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import AppIcon from '@/components/ui/AppIcon.vue'

export default {
  name: 'HomePage',
  components: {
    AboutPage,
    WorkPage,
    ResumePage,
    ContactPage,
    ProfileAvatar,
    RoleRotator,
    BaseBadge,
    BaseButton,
    AppIcon,
  },
  data() {
    return {
      siteState,
      images,
    }
  },
  computed: {
    profile() {
      return siteState.profile
    },
    stats() {
      return siteState.homeStats
    },
  },
}
</script>
